import { ExportOutlined, ImportOutlined } from "@ant-design/icons";
import { ResourceProps } from "@refinedev/core";

export const RESOURCE_NAMES: Record<string, string> = {
  paymentRequests: "api/v1/payment_requests",
  invoicePayables: "api/v1/invoice_payables",
  paymentOrders: "api/v1/payment_orders",
  invoiceReceivables: "api/v1/invoice_receivables",

  companies: "api/v1/companies",
  banks: "api/v1/banks/no_default_accounts",
  bankAccounts: "api/v1/bank_accounts/non_default",
  currencies: "api/v1/currencies",
  customers: "api/v1/users",
  transactionsHistory: "api/v1/transactions_history",
};

export const ADMIN_RESOURCE_NAMES = [
  RESOURCE_NAMES.invoicePayables,
  RESOURCE_NAMES.invoiceReceivables,
  RESOURCE_NAMES.customers,
];

export const STATUS_TRANSACTION: Record<string, string> = {
  PENDING: "Pending",
  COMPLETED: "Completed",
  FAILED: "Failed",
  CANCELLED: "Cancelled",
  BLOCKED: "Blocked",
  REJECTED: "Rejected",
  CONFIRMING: "Processing",
  CONFIRMED: "Confirmed",
};

export const STATUS: Record<string, string> = {
  REQUIRED: "REQUIRED",
  PENDING: "PENDING",
  LOW_RISK: "LOW_RISK",
  MEDIUM_RISK: "MEDIUM_RISK",
  HIGH_RISK: "HIGH_RISK",
  HIGH_RISK_FIR: "HIGH_RISK_FIR",
  HIGH_RISK_EDD: "HIGH_RISK_EDD",
};

export const USER_STATUS: Record<string, string> = {
  [STATUS.LOW_RISK]: "Verified",
  [STATUS.REQUIRED]: "Unverified",
  [STATUS.PENDING]: "Unverified",
  [STATUS.MEDIUM_RISK]: "Unverified",
  [STATUS.HIGH_RISK]: "Unverified",
  [STATUS.HIGH_RISK_FIR]: "Unverified",
  [STATUS.HIGH_RISK_EDD]: "Unverified",
};

export const COLOR_USER_STATUS: Record<string, string> = {
  [STATUS.LOW_RISK]: "#18B853",
  [STATUS.REQUIRED]: "#F28A28",
  [STATUS.PENDING]: "#F28A28",
  [STATUS.MEDIUM_RISK]: "#F28A28",
  [STATUS.HIGH_RISK]: "#F28A28",
  [STATUS.HIGH_RISK_FIR]: "#F28A28",
  [STATUS.HIGH_RISK_EDD]: "#F28A28",
};

export const EXCLUSIVE_CLIENT_RESOURCES = [RESOURCE_NAMES.transactionsHistory];

export const RESOURCES: ResourceProps[] = [
  {
    name: RESOURCE_NAMES.paymentRequests,
    list: "/payment-request",
    meta: {
      canDelete: true,
      label: "Send Money",
      icon: <ImportOutlined />,
    },
  },
  {
    name: RESOURCE_NAMES.invoicePayables,
    list: "/invoice-payables",
    meta: {
      canDelete: true,
      label: "Invoices (Payables)",
      icon: <ImportOutlined />,
    },
  },
  {
    name: RESOURCE_NAMES.paymentOrders,
    list: "/payment-order",
    meta: {
      canDelete: true,
      label: "Request Money",
      icon: <ExportOutlined />,
    },
  },
  {
    name: RESOURCE_NAMES.transactionsHistory,
    list: "/transactions-history",
    meta: {
      label: "Transactions history",
      icon: <ExportOutlined />,
    },
  },
  {
    name: RESOURCE_NAMES.invoiceReceivables,
    list: "/invoice-receivables",
    meta: {
      canDelete: true,
      label: "Invoices (Receivables)",
      icon: <ExportOutlined />,
    },
  },
  {
    name: RESOURCE_NAMES.companies,
    list: "/company",
    meta: {
      canDelete: true,
      label: "Provider",
      parent: "Directory",
    },
  },
  {
    name: RESOURCE_NAMES.banks,
    list: "/banks",
    meta: {
      canDelete: true,
      label: "Banks",
      parent: "Directory",
    },
  },
  {
    name: RESOURCE_NAMES.bankAccounts,
    list: "/bank-accounts",
    meta: {
      canDelete: true,
      label: "Customer accounts",
      parent: "Directory",
    },
  },
  {
    name: RESOURCE_NAMES.currencies,
    list: "/currencies",
    meta: {
      canDelete: true,
      label: "Currencies",
      parent: "Directory",
      hide: true,
    },
  },
  {
    name: RESOURCE_NAMES.customers,
    list: "/customers",
    meta: {
      label: "Customers",
      parent: "Directory",
    },
  },
];

export const TYPE_COMPANY = {
  BENEFICIARY: "BENEFICIARY",
  REMITTER: "REMITTER",
};

/** User storage folder names for upload-file API */
export const UserStorageFolder = {
  Avatar: "avatar",
  OnboardingSMARTForm: "onboarding-SMART-forms",
} as const;

/** Onboarding form status from /profile */
export const OnboardingStatus = {
  PENDING: "PENDING",
  INVITED: "INVITED",
  SUBMITTED: "SUBMITTED",
  REJECTED: "REJECTED",
  APPROVED: "APPROVED",
  BLOCKED: "BLOCKED",
  RESTRICTED: "RESTRICTED",
} as const;

export type OnboardingStatusType =
  (typeof OnboardingStatus)[keyof typeof OnboardingStatus];

export const ONBOARDING_STATUS_LABEL: Record<string, string> = {
  [OnboardingStatus.PENDING]: "Pending",
  [OnboardingStatus.INVITED]: "Invited",
  [OnboardingStatus.SUBMITTED]: "Submitted",
  [OnboardingStatus.REJECTED]: "Rejected",
  [OnboardingStatus.APPROVED]: "Approved",
  [OnboardingStatus.BLOCKED]: "Blocked",
  [OnboardingStatus.RESTRICTED]: "Restricted",
};

/** Short descriptions for onboarding statuses (UX/tooltips) */
export const ONBOARDING_STATUS_DESCRIPTION: Record<string, string> = {
  [OnboardingStatus.PENDING]: "Awaiting onboarding invite",
  [OnboardingStatus.INVITED]: "Onboarding invite email sent",
  [OnboardingStatus.SUBMITTED]: "User uploaded onboarding PDF",
  [OnboardingStatus.REJECTED]: "Admin rejected PDF; user may re-upload",
  [OnboardingStatus.APPROVED]: "Admin approved onboarding PDF",
  [OnboardingStatus.BLOCKED]: "Admin blocked user; user cannot login",
  [OnboardingStatus.RESTRICTED]:
    "User can login but cannot create new orders; can receive statement & contact us",
};

/** Status tag colors: red = negative, orange = pending/action, green = approved */
export const COLOR_ONBOARDING_STATUS: Record<string, string> = {
  [OnboardingStatus.PENDING]: "#F28A28",
  [OnboardingStatus.INVITED]: "#F28A28",
  [OnboardingStatus.SUBMITTED]: "#F28A28",
  [OnboardingStatus.REJECTED]: "#EC3333",
  [OnboardingStatus.APPROVED]: "#18B853",
  [OnboardingStatus.BLOCKED]: "#E53935",
  [OnboardingStatus.RESTRICTED]: "#EC3333",
};

export const STATUS_USER: Record<string, string> = {
  ACTIVE: "ACTIVE",
  RESTRICTED: "RESTRICTED",
  BLOCKED: "BLOCKED",
};

export const STATUS_USER_ENUM: Record<string, string> = {
  [STATUS_USER.ACTIVE]: "Active",
  [STATUS_USER.RESTRICTED]: "Restricted",
  [STATUS_USER.BLOCKED]: "Blocked",
};

export const COLOR_STATUS_USER: Record<string, string> = {
  [STATUS_USER.ACTIVE]: "#18B853",
  [STATUS_USER.RESTRICTED]: "#EC3333",
  [STATUS_USER.BLOCKED]: "#E53935",
};
